import { get, isEmpty, mapKeys } from "lodash"
import { useCallback, useRef } from "react"

import Firebase from "#classes/Firebase"
import { DEFAULT_USER_SETTINGS } from "#constants/globalConstants"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { type UserConstructor } from "#customTypes/composedTypes/userTypes"
import { UserSettings } from "#customTypes/globalTypes"
import { TypesafeAny } from "#customTypes/types"

const DEBOUNCE_MS = 500

const useUserSettings = (currentUser: UserConstructor | null) => {
  const pendingRef = useRef<Partial<UserSettings>>({})
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Dot-path keys so a flush only touches the settings it changed; a
  // full-map write would clobber settings saved from another tab/device.
  const flushToFirebase = useCallback((userId: string) => {
    const pending = pendingRef.current
    if (isEmpty(pending)) return

    pendingRef.current = {}
    void Firebase.getInstance().updateDocumentSilent(
      DOC_TYPES.USERS,
      userId,
      mapKeys(pending, (_value, key) => `settings.${key}`)
    )
  }, [])

  const scheduleFlush = useCallback(
    (userId: string) => {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        timerRef.current = null
        flushToFirebase(userId)
      }, DEBOUNCE_MS)
    },
    [flushToFirebase]
  )

  const getUserSetting = useCallback(
    <T extends TypesafeAny>(key: keyof UserSettings): T => {
      if (!currentUser) return DEFAULT_USER_SETTINGS[key] as T

      const pendingValue = pendingRef.current[key]
      if (pendingValue !== undefined) return pendingValue as T

      return (
        (get(currentUser.settings, key) as T) ??
        (DEFAULT_USER_SETTINGS[key] as T)
      )
    },
    [currentUser]
  )

  const setUserSetting = useCallback(
    (key: keyof UserSettings, value: TypesafeAny) => {
      if (!currentUser) return

      pendingRef.current = { ...pendingRef.current, [key]: value }
      scheduleFlush(currentUser.id)
    },
    [currentUser, scheduleFlush]
  )

  const setBulkUserSettings = useCallback(
    (settings: Partial<UserSettings>) => {
      if (!currentUser) return

      pendingRef.current = { ...pendingRef.current, ...settings }
      scheduleFlush(currentUser.id)
    },
    [currentUser, scheduleFlush]
  )

  return {
    getUserSetting,
    setUserSetting,
    setBulkUserSettings,
  }
}

export default useUserSettings
